// ── Global Sandbox mode (owner 2026-08-24) ──────────────────────────────────────────────────────
// A menu switch that puts THIS OPERATOR'S browser into sandbox mode. What the mode changes
// functionally is deliberately not decided yet ("沙盒模式下的功能改变我随后告诉你") — today it is
// the mode itself plus its visual marker: the top-left logo shifts colour so nobody can mistake
// which mode they are working in.
//
// Scope decisions, made explicit:
//   • PER BROWSER (localStorage), never a server-wide flag — one operator entering sandbox must not
//     recolour a colleague's screen mid-invoice.
//   • Only the HEADER logo recolours (className "app-logo"). The login page's centered logo and
//     every client-facing page (portal / workflow share / contract signing) keep the real brand —
//     a client must never see an off-colour logo because of a staff-side test switch.
//   • Every API request carries `X-Sandbox: 1` while the mode is on (see utils/api.ts). The server
//     ignores it today; when the owner defines the behaviour, the plumbing is already there.
//
// Module-level singleton, same pattern as uploadManager: routing swaps components, it does not
// re-evaluate modules, so the mode survives navigation and only ends with the tab / the toggle.

const KEY = 'mf_sandbox';

// Blue → PINK (owner 2026-08-24: "粉色的最好"). The shield's blue sits around hue 215°; rotating
// ~120° lands it in pink/magenta, brightened a touch so it reads as pink rather than wine-red.
const CSS = `
  body[data-sandbox="1"] img.app-logo { filter: hue-rotate(118deg) saturate(1.35) brightness(1.12); }
`;

let on = false;
const subs = new Set<(on: boolean) => void>();

function apply() {
  try {
    if (on) document.body.setAttribute('data-sandbox', '1');
    else document.body.removeAttribute('data-sandbox');
  } catch { /* SSR/tests without a DOM */ }
}

// Inject the rule once — the project has no global stylesheet (styles are inline JSX), so the
// mode's CSS lives with the mode instead of a lone .css file nothing else uses.
try {
  const tag = document.createElement('style');
  tag.setAttribute('data-sandbox-css', '1');
  tag.textContent = CSS;
  document.head.appendChild(tag);
  on = localStorage.getItem(KEY) === '1';
  apply();
} catch { /* no DOM */ }

export const isSandbox = () => on;

export function setSandbox(next: boolean): void {
  on = !!next;
  try { if (on) localStorage.setItem(KEY, '1'); else localStorage.removeItem(KEY); } catch { /* private mode */ }
  apply();
  subs.forEach(f => { try { f(on); } catch { /* a bad subscriber must not break the toggle */ } });
}

export const toggleSandbox = () => setSandbox(!on);

/** Subscribe to mode changes. Fires immediately with the current state; returns an unsubscribe. */
export function subscribeSandbox(fn: (on: boolean) => void): () => void {
  subs.add(fn); fn(on);
  return () => { subs.delete(fn); };
}
